import type { LocalDate, LogbookClass, LogbookEntry } from '@/lib/db';

/**
 * The grouping/totals engine — `PLAN.md` §6 Phase 6: "every total is a fold
 * over a filtered entry set, so build one aggregation engine and expose it
 * everywhere" (`NZ-LOGBOOK.md` §4). `totals.ts`'s running total is the fold
 * this was already built on; this file is the same fold applied to any
 * partition of the entry set instead of the whole thing in date order.
 *
 * Nothing here decides which entries count — same as `entryTotalHours` in
 * `columns.ts`, these functions sum whatever they are given. A draft counting
 * toward a dashboard total is a call-site mistake, not something this module
 * silently prevents twice; `confirmedOnly` below is the one place that
 * decision is made, and every caller in this feature routes through it.
 */

export interface CapacityTotals {
  /** `dayHr + nightHr` — the whole of a flight's airframe time, per `LogbookTimeMatrix`'s own contract. */
  totalHr: number;
  dayHr: number;
  nightHr: number;
  picHr: number;
  commandPracticeHr: number;
  dualHr: number;
  coPilotHr: number;
  instructorHr: number;
  actualInstrumentHr: number;
  simulatedInstrumentHr: number;
  groundInstrumentHr: number;
  ifrCrossCountryHr: number;
  approachesNonPrecision: number;
  approachesPrecision: number;
  landingsDay: number;
  landingsNight: number;
  landingsTouchAndGo: number;
  takeoffsDay: number;
  takeoffsNight: number;
  flightCount: number;
}

const ZERO_TOTALS: CapacityTotals = {
  totalHr: 0,
  dayHr: 0,
  nightHr: 0,
  picHr: 0,
  commandPracticeHr: 0,
  dualHr: 0,
  coPilotHr: 0,
  instructorHr: 0,
  actualInstrumentHr: 0,
  simulatedInstrumentHr: 0,
  groundInstrumentHr: 0,
  ifrCrossCountryHr: 0,
  approachesNonPrecision: 0,
  approachesPrecision: 0,
  landingsDay: 0,
  landingsNight: 0,
  landingsTouchAndGo: 0,
  takeoffsDay: 0,
  takeoffsNight: 0,
  flightCount: 0,
};

/** Only a confirmed entry counts toward a total or a currency check — `NZ-LOGBOOK.md` §2.3, same rule `totals.ts`'s running total already applies. */
export function confirmedOnly(entries: readonly LogbookEntry[]): LogbookEntry[] {
  return entries.filter((entry) => entry.confirmedAt !== undefined);
}

/** Sums every field the CAR 61.29 time matrix and landings carry, over any entry set. */
export function sumTotals(entries: readonly LogbookEntry[]): CapacityTotals {
  return entries.reduce<CapacityTotals>((sum, entry) => {
    const { time, approaches, landings, takeoffs } = entry;
    return {
      totalHr: sum.totalHr + time.dayHr + time.nightHr,
      dayHr: sum.dayHr + time.dayHr,
      nightHr: sum.nightHr + time.nightHr,
      picHr: sum.picHr + time.picHr,
      commandPracticeHr: sum.commandPracticeHr + time.commandPracticeHr,
      dualHr: sum.dualHr + time.dualHr,
      coPilotHr: sum.coPilotHr + time.coPilotHr,
      instructorHr: sum.instructorHr + time.instructorHr,
      actualInstrumentHr: sum.actualInstrumentHr + time.actualInstrumentHr,
      simulatedInstrumentHr: sum.simulatedInstrumentHr + time.simulatedInstrumentHr,
      groundInstrumentHr: sum.groundInstrumentHr + time.groundInstrumentHr,
      ifrCrossCountryHr: sum.ifrCrossCountryHr + time.ifrCrossCountryHr,
      approachesNonPrecision: sum.approachesNonPrecision + approaches.nonPrecision,
      approachesPrecision: sum.approachesPrecision + approaches.precision,
      landingsDay: sum.landingsDay + landings.day,
      landingsNight: sum.landingsNight + landings.night,
      landingsTouchAndGo: sum.landingsTouchAndGo + landings.touchAndGo,
      takeoffsDay: sum.takeoffsDay + takeoffs.day,
      takeoffsNight: sum.takeoffsNight + takeoffs.night,
      flightCount: sum.flightCount + 1,
    };
  }, ZERO_TOTALS);
}

const UNKNOWN = '(unknown)';

/** Grouping key functions — one per dimension `NZ-LOGBOOK.md` §4 names. Each reads only what a manual entry always has, so an entry with no linked `Aircraft` record still groups sensibly. */
export const GROUP_BY = {
  registration: (entry: LogbookEntry): string => entry.registration ?? UNKNOWN,
  aircraftType: (entry: LogbookEntry): string => entry.aircraftType ?? UNKNOWN,
  engineClass: (entry: LogbookEntry): LogbookClass => entry.time.engineClass,
  month: (entry: LogbookEntry): string => entry.date.slice(0, 7),
  year: (entry: LogbookEntry): string => entry.date.slice(0, 4),
} as const;

export function groupBy<K extends string>(
  entries: readonly LogbookEntry[],
  keyFn: (entry: LogbookEntry) => K,
): Map<K, LogbookEntry[]> {
  const groups = new Map<K, LogbookEntry[]>();
  for (const entry of entries) {
    const key = keyFn(entry);
    const bucket = groups.get(key);
    if (bucket === undefined) {
      groups.set(key, [entry]);
    } else {
      bucket.push(entry);
    }
  }
  return groups;
}

export interface GroupTotal<K extends string> {
  key: K;
  totals: CapacityTotals;
}

/** `groupBy` plus the fold, sorted by key — the shape every grouped table in the totals dashboard reads from. */
export function groupTotals<K extends string>(
  entries: readonly LogbookEntry[],
  keyFn: (entry: LogbookEntry) => K,
): GroupTotal<K>[] {
  return [...groupBy(entries, keyFn).entries()]
    .map(([key, group]) => ({ key, totals: sumTotals(group) }))
    .sort((a, b) => a.key.localeCompare(b.key));
}

/** Inclusive on both ends; `from`/`to` are local calendar dates. */
export function filterDateRange(
  entries: readonly LogbookEntry[],
  from: LocalDate,
  to: LocalDate,
): LogbookEntry[] {
  return entries.filter((entry) => entry.date >= from && entry.date <= to);
}

/** `asOf` minus `days - 1`, so the window is exactly `days` calendar days inclusive of `asOf` itself. */
function windowStart(asOf: LocalDate, days: number): LocalDate {
  const date = new Date(`${asOf}T00:00:00.000Z`);
  date.setUTCDate(date.getUTCDate() - (days - 1));
  return date.toISOString().slice(0, 10);
}

/** The last 90 days ending on `asOf` — `NZ-LOGBOOK.md` §4/§5's recent-experience window. */
export function filterLast90Days(
  entries: readonly LogbookEntry[],
  asOf: LocalDate,
): LogbookEntry[] {
  return filterDateRange(entries, windowStart(asOf, 90), asOf);
}

/** The 12 months ending on `asOf`, calendar-accurate (not a fixed 365 days) — `NZ-LOGBOOK.md` §4's "rolling 12 months". */
export function filterRolling12Months(
  entries: readonly LogbookEntry[],
  asOf: LocalDate,
): LogbookEntry[] {
  const date = new Date(`${asOf}T00:00:00.000Z`);
  date.setUTCMonth(date.getUTCMonth() - 12);
  date.setUTCDate(date.getUTCDate() + 1);
  return filterDateRange(entries, date.toISOString().slice(0, 10), asOf);
}

export interface CrossTab<R extends string, C extends string> {
  rowKeys: R[];
  colKeys: C[];
  cellTotals: (row: R, col: C) => CapacityTotals | undefined;
}

/** "Hours by type by year" — `NZ-LOGBOOK.md` §4's own example of the view most logbook apps get wrong. */
export function crossTab<R extends string, C extends string>(
  entries: readonly LogbookEntry[],
  rowKeyFn: (entry: LogbookEntry) => R,
  colKeyFn: (entry: LogbookEntry) => C,
): CrossTab<R, C> {
  const cellEntries = new Map<string, LogbookEntry[]>();
  const rowKeys = new Set<R>();
  const colKeys = new Set<C>();
  for (const entry of entries) {
    const row = rowKeyFn(entry);
    const col = colKeyFn(entry);
    rowKeys.add(row);
    colKeys.add(col);
    const cellKey = `${row} ${col}`;
    const bucket = cellEntries.get(cellKey);
    if (bucket === undefined) {
      cellEntries.set(cellKey, [entry]);
    } else {
      bucket.push(entry);
    }
  }
  return {
    rowKeys: [...rowKeys].sort((a, b) => a.localeCompare(b)),
    colKeys: [...colKeys].sort((a, b) => a.localeCompare(b)),
    cellTotals: (row, col) => {
      const bucket = cellEntries.get(`${row} ${col}`);
      return bucket === undefined ? undefined : sumTotals(bucket);
    },
  };
}
